import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode
} from "react";
import { cn } from "../../lib/cn";
import { useFieldControlProps } from "./field";

const EMPTY_OPTION_VALUE = "__listwork_empty_select_value__";

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const controlProps = useFieldControlProps(props);

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn("ui-input ui-select-trigger", className)}
      {...controlProps}
    >
      {children}
      <SelectPrimitive.Icon className="ui-select-icon">
        <ChevronDown aria-hidden="true" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", sideOffset = 6, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn("ui-select-content", className)}
      position={position}
      sideOffset={sideOffset}
      {...props}
    >
      <SelectPrimitive.ScrollUpButton className="ui-select-scroll-button">
        <ChevronUp aria-hidden="true" />
      </SelectPrimitive.ScrollUpButton>
      <SelectPrimitive.Viewport className="ui-select-viewport">
        {children}
      </SelectPrimitive.Viewport>
      <SelectPrimitive.ScrollDownButton className="ui-select-scroll-button">
        <ChevronDown aria-hidden="true" />
      </SelectPrimitive.ScrollDownButton>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));

SelectContent.displayName = SelectPrimitive.Content.displayName;

export const SelectLabel = forwardRef<
  ElementRef<typeof SelectPrimitive.Label>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={cn("ui-select-label", className)} {...props} />
));

SelectLabel.displayName = SelectPrimitive.Label.displayName;

export const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  Omit<ComponentPropsWithoutRef<typeof SelectPrimitive.Item>, "value"> & { value: string }
>(({ className, children, value, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn("ui-select-item", className)}
    value={toRadixValue(value)}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator className="ui-select-item-indicator">
      <Check aria-hidden="true" />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));

SelectItem.displayName = SelectPrimitive.Item.displayName;

export const SelectSeparator = forwardRef<
  ElementRef<typeof SelectPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn("ui-select-separator", className)} {...props} />
));

SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export interface SelectOption {
  disabled?: boolean;
  label: ReactNode;
  value: string;
}

export interface SelectControlProps
  extends Omit<ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>, "children" | "value"> {
  defaultValue?: string;
  name?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  value?: string;
}

export const SelectControl = forwardRef<HTMLButtonElement, SelectControlProps>(
  (
    {
      defaultValue,
      disabled,
      name,
      onValueChange,
      options,
      placeholder,
      required,
      value,
      ...triggerProps
    },
    ref
  ) => (
    <Select
      defaultValue={defaultValue === undefined ? undefined : controlValue(defaultValue, options)}
      disabled={disabled}
      name={name}
      onValueChange={(nextValue) => onValueChange?.(fromRadixValue(nextValue))}
      required={required}
      value={value === undefined ? undefined : controlValue(value, options)}
    >
      <SelectTrigger ref={ref} disabled={disabled} aria-required={required || undefined} {...triggerProps}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.map((option) => (
            <SelectItem
              key={`${option.value}-${String(option.label)}`}
              disabled={option.disabled}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
);

SelectControl.displayName = "SelectControl";

function toRadixValue(value: string) {
  return value === "" ? EMPTY_OPTION_VALUE : value;
}

function controlValue(value: string, options: SelectOption[]) {
  // Radix shows the placeholder for an empty selection. The sentinel is only
  // needed when an actual option represents an empty value (for example All).
  return value === "" && !options.some((option) => option.value === "") ? "" : toRadixValue(value);
}

function fromRadixValue(value: string) {
  return value === EMPTY_OPTION_VALUE ? "" : value;
}
